import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  buildAccountCloudTypeLookup,
  getAccountIdentifier,
} from "../../../CloudProviderContext";
import { useAccounts } from "../../../components/Common/HelperComponents/GetDataFromStore";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
  auditHubPageSx,
  outlinedButtonSx,
  searchFieldSx,
} from "../../Compliance/AuditHub/theme";
import {
  getAwsCustomerAccountEntries,
  getCustomerPayers,
  getUniqueCustomerPayers,
  resolveAccountPayer,
} from "../utils/customerHelpers";

const asTrimmed = (value) => (value == null ? "" : String(value).trim());

/** Payer metadata keyed by linked account number from `account_payers`. */
const accountPayersByNumber = (customer) => {
  const raw = customer?.account_payers;
  const map = new Map();
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return map;
  Object.entries(raw).forEach(([key, item]) => {
    const accountNumber = asTrimmed(
      item?.account_number ?? item?.accountNumber ?? key
    );
    if (!/^\d{12}$/.test(accountNumber)) return;
    map.set(accountNumber, {
      payerAccountId: asTrimmed(
        item?.payer_account_id ?? item?.payerAccountId
      ),
      payerAccountName: asTrimmed(
        item?.payer_account_name ?? item?.payerAccountName
      ),
      accountName: asTrimmed(item?.account_name ?? item?.accountName),
    });
  });
  return map;
};

/**
 * Build display rows: payers first (accent), then payees with their payer line.
 * Uses margin-summary `account_payers` when present.
 */
const buildAccountRows = (customer, cloudTypeLookup, accountsByNumber) => {
  if (!customer) return [];

  const payerMeta = accountPayersByNumber(customer);
  const payerIdSet = new Set(
    getUniqueCustomerPayers(customer)
      .map((payer) => payer.id)
      .filter(Boolean)
  );

  // Any account that appears as someone else's payer_account_id is a payer.
  payerMeta.forEach((meta) => {
    if (meta.payerAccountId) payerIdSet.add(meta.payerAccountId);
  });
  getCustomerPayers(customer).forEach((payer) => {
    if (payer?.id) payerIdSet.add(payer.id);
  });

  return getAwsCustomerAccountEntries(customer, cloudTypeLookup).map((entry) => {
    const accountNumber = asTrimmed(entry.accountNumber);
    const fromMap = payerMeta.get(accountNumber);
    const fromStore = accountsByNumber.get(accountNumber);
    const resolved = resolveAccountPayer(customer, accountNumber, [
      fromStore,
      entry,
      fromMap
        ? {
            payer_account_id: fromMap.payerAccountId,
            payer_account_name: fromMap.payerAccountName,
          }
        : null,
    ]);

    const payerId =
      asTrimmed(fromMap?.payerAccountId) ||
      asTrimmed(entry.payerAccountId) ||
      asTrimmed(resolved?.id);
    const payerName =
      asTrimmed(fromMap?.payerAccountName) ||
      asTrimmed(entry.payerAccountName) ||
      asTrimmed(resolved?.name);
    const accountName =
      asTrimmed(fromMap?.accountName) ||
      asTrimmed(entry.accountName) ||
      asTrimmed(fromStore?.account_name ?? fromStore?.accountName);
    const isPayer =
      (payerId && payerId === accountNumber) || payerIdSet.has(accountNumber);

    return {
      accountNumber,
      company: entry.company || customer?.customer_name || "",
      accountName,
      status: entry.status || "",
      isPayer,
      payerId: payerId && payerId !== accountNumber ? payerId : isPayer ? accountNumber : "",
      payerName,
    };
  }).sort((a, b) => {
    if (a.isPayer !== b.isPayer) return a.isPayer ? -1 : 1;
    return String(a.accountNumber).localeCompare(String(b.accountNumber));
  });
};

const roleChipSx = (isPayer) => ({
  height: 22,
  fontSize: "0.7rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: isPayer ? "#fff4eb" : "#f8fafc",
  color: isPayer ? AH_ACCENT : AH_MUTED,
  border: `1px solid ${isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
  "& .MuiChip-label": { px: 1 },
});

const CustomerAccountsModal = ({ open, onClose, customer }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const orgAccounts = useAccounts();
  const cloudTypeLookup = useMemo(
    () => buildAccountCloudTypeLookup(orgAccounts),
    [orgAccounts]
  );

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open, customer]);

  const accountsByNumber = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(orgAccounts)) return map;
    orgAccounts.forEach((account) => {
      const id = getAccountIdentifier(account);
      if (id) map.set(id, account);
    });
    return map;
  }, [orgAccounts]);

  const accountRows = useMemo(
    () => buildAccountRows(customer, cloudTypeLookup, accountsByNumber),
    [customer, cloudTypeLookup, accountsByNumber]
  );

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return accountRows;
    return accountRows.filter((row) => {
      const haystack = [
        row.accountNumber,
        row.company,
        row.accountName,
        row.payerId,
        row.payerName,
        row.isPayer ? "payer" : "payee",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [accountRows, searchTerm]);

  const titleName = customer?.customer_name || customer?.orgcode || "Customer";

  return (
    <Dialog
      open={open && Boolean(customer)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="customer-accounts-modal-title"
      PaperProps={{
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: 600,
          maxHeight: { xs: "92vh", sm: "88vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          flexShrink: 0,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="customer-accounts-modal-title"
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              color: AH_WHITE,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            AWS Accounts — {titleName}
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: AH_WHITE,
              opacity: 0.92,
            }}
          >
            {accountRows.length} account{accountRows.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          ×
        </Box>
      </Box>

      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: AH_WHITE,
          flex: "1 1 auto",
          overflowY: "auto",
        }}
      >
        {accountRows.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: AH_MUTED, textAlign: "center", py: 4 }}>
            No AWS accounts found for this customer.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                mb: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: AH_MUTED,
                  textTransform: "uppercase",
                }}
              >
                Linked accounts
              </Typography>
              <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
                {filteredRows.length} of {accountRows.length}
              </Typography>
            </Box>

            <TextField
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search account, name, or payer…"
              aria-label="Search linked accounts"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...searchFieldSx, width: "100%", mb: 2 }}
            />

            {filteredRows.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: AH_MUTED, textAlign: "center", py: 3 }}>
                No accounts match your search.
              </Typography>
            ) : (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {filteredRows.map((row) => {
                  const payerRefLabel =
                    !row.isPayer && row.payerId
                      ? row.payerName
                        ? `Payer: ${row.payerId} · ${row.payerName}`
                        : `Payer: ${row.payerId}`
                      : "";
                  return (
                    <Box
                      component="li"
                      key={row.accountNumber}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: "8px",
                        border: `1px solid ${row.isPayer ? `${AH_ACCENT}55` : AH_BORDER}`,
                        backgroundColor: row.isPayer ? "#fff4eb" : "#f8fafc",
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            fontFamily: "monospace",
                            color: AH_TEXT,
                            lineHeight: 1.35,
                          }}
                        >
                          {row.isPayer ? `Payer: ${row.accountNumber}` : row.accountNumber}
                        </Typography>
                        {row.accountName || row.company ? (
                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: 12,
                              color: AH_MUTED,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.accountName || row.company}
                          >
                            {row.accountName || row.company}
                          </Typography>
                        ) : null}
                        {payerRefLabel ? (
                          <Chip
                            size="small"
                            label={payerRefLabel}
                            title={payerRefLabel}
                            sx={{
                              mt: 0.75,
                              height: 22,
                              maxWidth: "100%",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              fontFamily: "monospace",
                              borderRadius: "6px",
                              backgroundColor: "#fff4eb",
                              color: AH_ACCENT,
                              border: `1px solid ${AH_ACCENT}55`,
                              "& .MuiChip-label": {
                                px: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                      <Chip
                        size="small"
                        label={row.isPayer ? "Payer" : "Payee"}
                        sx={{ ...roleChipSx(row.isPayer), flexShrink: 0, mt: 0.15 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          py: 2,
          borderTop: `1px solid ${AH_BORDER}`,
          backgroundColor: "#f8fafc",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...outlinedButtonSx,
            height: 36,
            minHeight: 36,
            backgroundColor: "#74788d",
            color: AH_WHITE,
            borderColor: "#74788d",
            "&:hover": {
              backgroundColor: "#636678",
              borderColor: "#636678",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

CustomerAccountsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
};

export default CustomerAccountsModal;
